import React, { useState } from 'react';
import { ConversationMetrics } from '../types';

interface Props {
  metrics: ConversationMetrics;
}

export function MetricsPanel({ metrics }: Props) {
  const [open, setOpen] = useState(false);

  const fmt = (ms: number | undefined) => ms != null ? `${ms.toFixed(0)}ms` : '—';
  const fmtCost = (c: number | undefined) => c == null ? '—' : c < 0.0001 ? '<$0.0001' : `$${c.toFixed(4)}`;
  const fmtRtf = (r: number | undefined) => r != null ? r.toFixed(2) + 'x' : '—';
  const fmtWer = (w: number | undefined) => w != null ? (w * 100).toFixed(1) + '%' : '—';

  return (
    <div style={styles.container}>
      <button style={styles.toggle} onClick={() => setOpen(!open)}>
        ⚡ {fmt(metrics.totalLatencyMs)} · {fmtCost(metrics.totalCost)}
        {metrics.wer !== undefined && ` · WER ${fmtWer(metrics.wer)}`}
        <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={styles.details}>
          {metrics.stt && (
            <Row label={`STT (${metrics.stt.provider})`}
              cols={[fmt(metrics.stt.processingTimeMs), `RTF ${fmtRtf(metrics.stt.rtf)}`, fmtCost(metrics.stt.cost)]} />
          )}
          <Row label={`LLM (${metrics.llm.model || 'gpt-4.1'})`}
            cols={[fmt(metrics.llm.processingTimeMs), `${metrics.llm.inputTokens}in${metrics.llm.cachedInputTokens ? ` (${metrics.llm.cachedInputTokens} cached)` : ''} / ${metrics.llm.outputTokens}out tok`, fmtCost(metrics.llm.cost)]} />
          {metrics.tts && (
            <Row label={`TTS (${metrics.tts.provider})`}
              cols={[fmt(metrics.tts.processingTimeMs), `${metrics.tts.characterCount} chars`, fmtCost(metrics.tts.cost)]} />
          )}
          {metrics.wer !== undefined && (
            <Row label="WER" cols={[fmtWer(metrics.wer), '', '']} />
          )}
          <div style={styles.total}>
            Total: {fmt(metrics.totalLatencyMs)} · {fmtCost(metrics.totalCost)}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, cols }: { label: string; cols: string[] }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, marginBottom: 4 }}>
      <span style={{ color: '#666', minWidth: 140 }}>{label}</span>
      {cols.map((c, i) => <span key={i} style={{ color: '#333' }}>{c}</span>)}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { marginTop: 6 },
  toggle: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 11,
    color: '#888',
    padding: '2px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  chevron: { marginLeft: 2 },
  details: {
    marginTop: 8,
    padding: '10px 12px',
    background: '#f8f9fa',
    borderRadius: 8,
    border: '1px solid #eee',
    maxWidth: 420,
    width: 'fit-content',
  },
  total: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid #e0e0e0',
    fontSize: 12,
    fontWeight: 600,
    color: '#444',
  },
};
